import BookmakerAssetModel from "../Bookmakers/BookmakerAssetModel";
import BookmakerBrandingModel from "../Bookmakers/BookmakerBrandingModel";
import BookmakerModel from "../Bookmakers/BookmakerModel";
import BookmakerUrlModel from "../Bookmakers/BookmakerUrlModel";
import MatchOddsMarketModel from "../Markets/MatchOddsMarketModel";
import MatchOddsMarketScopeModel from "../Markets/MatchOddsMarketScopeModel";
import MatchOddsMarketSelectionModel from "../Markets/MatchOddsMarketSelectionModel";
import MatchOddsMarketTypeModel from "../Markets/MatchOddsMarketTypeModel";
import SelectionProviderInfoModel from "../Markets/SelectionProviderInfoModel";
import MatchOddsModel from "../MatchOddsModel";

export default class MatchOddsRemapper {
  fromResponse(
    response: any,
    overGoals: string[]
  ): Map<string, MatchOddsModel[]> {
    const model = new Map<string, MatchOddsModel[]>();

    response.matches.forEach((match: any) => {
      const oddsMatches: MatchOddsModel[] = match.odds.map((odds: any) => {
        const oddsModel = new MatchOddsModel();

        oddsModel.bookmaker = odds.bookmaker
          ? this.remapBookmaker(odds.bookmaker)
          : null;
        oddsModel.type = odds.type;
        oddsModel.markets = odds.markets
          ? this.remapMarkets(odds.markets, overGoals)
          : [];

        return oddsModel;
      });

      model.set(match.id, oddsMatches);
    });

    return model;
  }

  private remapBookmaker(bookmaker: any) {
    const model = new BookmakerModel();

    model.id = bookmaker.id;
    model.name = bookmaker.name;
    model.assets = bookmaker.assets ? this.remapAssets(bookmaker.assets) : [];
    model.links = bookmaker.links || [];
    model.branding = bookmaker.branding
      ? this.remapBranding(bookmaker.branding)
      : null;
    model.eventUrls = bookmaker.event_urls
      ? this.remapEventUrls(bookmaker.event_urls)
      : [];
    model.stakesDisclaimer = bookmaker.stakes_disclaimer;
    model.domainName = bookmaker.domain_name;

    return model;
  }

  private remapMarkets(markets: any[], overGoals: string[]) {
    return markets.map((market) => {
      const model = new MatchOddsMarketModel();

      model.type = market.type ? this.remapMarketType(market.type) : null;
      model.scope = market.scope ? this.remapMarketScope(market.scope) : null;
      model.selections = market.selections
        ? this.remapMarketSelections(
            market.selections,
            model.type.code,
            overGoals
          )
        : [];

      return model;
    });
  }

  private remapEventUrls(eventUrls: any[]) {
    return eventUrls.map((eventUrl) => {
      const model = new BookmakerUrlModel();

      model.url = eventUrl.url;
      model.appType = eventUrl.app_type;

      return model;
    });
  }

  private remapBranding(branding: any) {
    const model = new BookmakerBrandingModel();

    model.backgroundColor = branding.background_color;
    model.textColor = branding.text_color;

    return model;
  }

  private remapAssets(assets: any[]) {
    return assets.map((a) => {
      const model = new BookmakerAssetModel();

      model.logo = a.logo;
      model.type = a.type;
      model.transparentBackgroundUrl = a.transparent_background_url;
      model.backgroundColor = a.background_color;

      return model;
    });
  }

  private remapMarketType = (type: any) => {
    const model = new MatchOddsMarketTypeModel();

    model.id = type.id;
    model.name = type.name;
    model.code = type.code;
    model.entityType = type.entity_type;

    return model;
  };

  private remapMarketScope = (scope: any) => {
    const model = new MatchOddsMarketScopeModel();

    model.id = scope.id;
    model.name = scope.name;
    model.type = scope.type;
    model.entityType = scope.entity_type;

    return model;
  };

  private remapMarketSelections = (
    selections: any[],
    marketTypeCode: string,
    overGoals: string[]
  ) => {
    let filteredSelections = Array.from(selections);

    if (overGoals.length > 0 && marketTypeCode === "OVER_UNDER") {
      filteredSelections = filteredSelections.filter((s) => {
        if (s.code === "over") {
          return overGoals.some((goalMarket) => {
            switch (goalMarket) {
              case "OVER_GOALS_0_5":
                return s.value === "0.5";
              case "OVER_GOALS_1_5":
                return s.value === "1.5";
              case "OVER_GOALS_2_5":
                return s.value === "2.5";
              case "OVER_GOALS_3_5":
                return s.value === "3.5";
              case "OVER_GOALS_4_5":
                return s.value === "4.5";
              case "OVER_GOALS_5_5":
                return s.value === "5.5";
              default:
                return false;
            }
          });
        }
        return false;
      });
    }

    return filteredSelections.map((selection) => {
      const model = new MatchOddsMarketSelectionModel();

      model.id = selection.id;
      model.name = selection.name;
      model.code = selection.code;
      model.value = selection.value;
      model.odds = selection.odds;
      model.urls = selection.urls ? this.remapEventUrls(selection.urls) : [];
      model.movement = selection.movement;
      model.oddsOld = selection.odds_old;
      model.entityType = selection.entity_type;
      model.additionalInfo = selection.additional_info;
      model.providerInfo = selection.provider_info
        ? this.remapProviderInfo(selection.provider_info)
        : null;

      return model;
    });
  };

  private remapProviderInfo = (providerInfo: any) => {
    const model = new SelectionProviderInfoModel();

    model.selectionId = providerInfo.selection_id;
    model.eventId = providerInfo.event_id;
    model.marketId = providerInfo.market_id;
    model.competitionId = providerInfo.competition_id;

    return model;
  };
}
